import { useEffect, useState } from 'react';
import { DatePicker, Select, Table, Tag, theme, type TableProps } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { TransactionRecord } from '@/types/api';
import TableContainer from '@/components/common/TableContainer';
import { formatDate } from '@/utils/dateUtils';

const { RangePicker } = DatePicker;

/**
 * Server-side filter state for the vendor transaction history (#87).
 * Mirrors the query params of GET /api/vendors/:vendorId/transactions (#66).
 */
export interface TransactionFilterState {
  transactionType?: string;
  status?: string;
  startDate?: string; // YYYY-MM-DD, inclusive
  endDate?: string; // YYYY-MM-DD, inclusive
}

interface TransactionsSectionProps {
  transactions: TransactionRecord[] | null | undefined;
  isLoading: boolean;
  filters: TransactionFilterState;
  onFiltersChange: (filters: TransactionFilterState) => void;
}

export const TransactionsSection = ({
  transactions,
  isLoading,
  filters,
  onFiltersChange,
}: TransactionsSectionProps) => {
  const { t } = useTranslation('vendors');
  const { token } = theme.useToken();

  // Distinct type/status options derived from the loaded data (the ticket asks
  // 看有哪些分類/狀態). Accumulated stickily across loads so applying one filter
  // does not collapse the other select's options to the filtered subset — the
  // first (unfiltered) page seeds the full set.
  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);

  useEffect(() => {
    if (!transactions || transactions.length === 0) return;
    setTypeOptions((prev) => {
      const next = new Set(prev);
      transactions.forEach((txn) => next.add(txn.transactionType));
      return next.size === prev.length ? prev : Array.from(next);
    });
    setStatusOptions((prev) => {
      const next = new Set(prev);
      transactions.forEach((txn) => next.add(txn.status));
      return next.size === prev.length ? prev : Array.from(next);
    });
  }, [transactions]);

  const rangeValue: [Dayjs, Dayjs] | null =
    filters.startDate && filters.endDate
      ? [dayjs(filters.startDate), dayjs(filters.endDate)]
      : null;

  const transactionColumns: TableProps<TransactionRecord>['columns'] = [
    {
      title: t('vendor.table.transactionId'),
      dataIndex: 'transactionId',
      key: 'transactionId',
      render: (transactionId: number) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>TXN-{transactionId}</span>
      ),
    },
    {
      title: t('vendor.table.date'),
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => formatDate(date),
    },
    {
      title: t('vendor.table.type'),
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: TransactionRecord['transactionType']) => type,
    },
    {
      title: t('vendor.table.invoicePo'),
      key: 'reference',
      render: (_: unknown, record: TransactionRecord) =>
        record.invoiceNumber || record.purchaseOrder || '-',
    },
    {
      title: t('vendor.table.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: TransactionRecord) =>
        `${record.currency} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
    },
    {
      title: t('vendor.fields.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: TransactionRecord['status']) => {
        const statusConfig: Record<string, { color: string; icon: JSX.Element }> = {
          Pending: { color: 'gold', icon: <ClockCircleOutlined /> },
          Paid: { color: 'green', icon: <CheckCircleOutlined /> },
          Overdue: { color: 'red', icon: <CloseCircleOutlined /> },
          Cancelled: { color: 'default', icon: <CloseCircleOutlined /> },
        };
        const config = statusConfig[status] || statusConfig.Pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {status}
          </Tag>
        );
      },
    },
  ];

  return (
    <div>
      <h3
        style={{
          fontSize: token.fontSizeLG,
          fontWeight: token.fontWeightStrong,
          margin: 0,
          marginBottom: '16px',
        }}
      >
        {t('vendor.sections.transactionHistory')}
      </h3>
      <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <RangePicker
          size="large"
          value={rangeValue}
          allowClear
          placeholder={[
            t('vendor.transactionFilters.startDate'),
            t('vendor.transactionFilters.endDate'),
          ]}
          onChange={(range) =>
            onFiltersChange({
              ...filters,
              startDate: range?.[0] ? range[0].format('YYYY-MM-DD') : undefined,
              endDate: range?.[1] ? range[1].format('YYYY-MM-DD') : undefined,
            })
          }
        />
        <Select
          size="large"
          allowClear
          value={filters.transactionType}
          placeholder={t('vendor.transactionFilters.allTypes')}
          style={{ width: '180px' }}
          options={typeOptions.map((value) => ({ label: value, value }))}
          onChange={(value?: string) =>
            onFiltersChange({ ...filters, transactionType: value })
          }
        />
        <Select
          size="large"
          allowClear
          value={filters.status}
          placeholder={t('vendor.transactionFilters.allStatuses')}
          style={{ width: '180px' }}
          options={statusOptions.map((value) => ({ label: value, value }))}
          onChange={(value?: string) => onFiltersChange({ ...filters, status: value })}
        />
      </div>
      <TableContainer>
        <Table<TransactionRecord>
          columns={transactionColumns}
          dataSource={transactions || []}
          rowKey="transactionId"
          loading={isLoading}
          pagination={{
            pageSize: 10,
          }}
          scroll={{ x: 'max-content' }}
        />
      </TableContainer>
    </div>
  );
};
